import { CheckCircle2, CircleAlert, X } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { ToastContext } from './useToast'
import type { Notify, Tone } from './useToast'

interface Toast {
  id: number
  message: string
  tone: Tone
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(0)

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), [])
  const notify = useCallback<Notify>(
    (message, tone = 'success') => {
      const id = nextId.current++
      setToasts((t) => [...t.slice(-3), { id, message, tone }])
      window.setTimeout(() => dismiss(id), tone === 'error' ? 6000 : 3500)
    },
    [dismiss],
  )
  const value = useMemo(() => notify, [notify])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-[100] flex w-[min(24rem,calc(100%-2rem))] flex-col gap-2" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="rise-in pointer-events-auto flex items-start gap-3 border border-line bg-raised px-4 py-3 text-[14px] text-ink shadow-soft"
            role={t.tone === 'error' ? 'alert' : 'status'}
          >
            {t.tone === 'error' ? (
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
            ) : (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            )}
            <span className="flex-1">{t.message}</span>
            <button className="text-ink-3 hover:text-ink" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
